
import { useState, useCallback, useMemo } from "react";

export type SelectableItem = { id: string; type: "file" | "folder" };

export function useSelection() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  );

  const select = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setLastSelectedId(id);
  }, []);

  const deselect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const toggle = useCallback(
    (id: string) => {
      if (isSelected(id)) {
        deselect(id);
      } else {
        select(id);
      }
    },
    [isSelected, select, deselect]
  );

  const selectOnly = useCallback((id: string) => {
    setSelectedIds(new Set([id]));
    setLastSelectedId(id);
  }, []);

  const selectMultiple = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
    if (ids.length > 0) {
      setLastSelectedId(ids[ids.length - 1]);
    }
  }, []);

  const selectRange = useCallback(
    (items: SelectableItem[], targetId: string) => {
      if (!lastSelectedId) {
        selectOnly(targetId);
        return;
      }

      const itemIds = items.map((i) => i.id);
      const startIndex = itemIds.indexOf(lastSelectedId);
      const endIndex = itemIds.indexOf(targetId);

      if (startIndex === -1 || endIndex === -1) {
        selectOnly(targetId);
        return;
      }

      const [from, to] = startIndex < endIndex
        ? [startIndex, endIndex]
        : [endIndex, startIndex];

      const rangeIds = itemIds.slice(from, to + 1);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        rangeIds.forEach((id) => next.add(id));
        return next;
      });
    },
    [lastSelectedId, selectOnly]
  );

  const selectAll = useCallback((items: SelectableItem[]) => {
    const ids = items.map((i) => i.id);
    setSelectedIds(new Set(ids));
    if (ids.length > 0) {
      setLastSelectedId(ids[ids.length - 1]);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, []);

  const handleClick = useCallback(
    (
      id: string,
      items: SelectableItem[],
      event: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }
    ) => {
      const isCtrlClick = event.ctrlKey || event.metaKey;
      const isShiftClick = event.shiftKey;

      if (isShiftClick && lastSelectedId) {
        selectRange(items, id);
      } else if (isCtrlClick) {
        toggle(id);
      } else {
        selectOnly(id);
      }
    },
    [toggle, selectOnly, selectRange, lastSelectedId]
  );

  const selectedCount = selectedIds.size;
  const hasSelection = selectedCount > 0;

  const selectedIdArray = useMemo(
    () => Array.from(selectedIds),
    [selectedIds]
  );

  return {
    selectedIds,
    selectedIdArray,
    selectedCount,
    hasSelection,
    lastSelectedId,
    isSelected,
    select,
    deselect,
    toggle,
    selectOnly,
    selectMultiple,
    selectRange,
    selectAll,
    clearSelection,
    handleClick,
  };
}
